/**
 * The roster, at the top of the score screen. SPEC.md §14.1, §7.3, §8.1, §45.
 *
 * Two things, and deliberately nothing else: which hand is being played, and
 * where everybody stands. It is the only place on this screen showing anyone's
 * score, so it is also what the ring used to be — a glance that answers "am I
 * winning" without leaving the keypad. §7.1
 *
 * It used to be a row of text chips and is now a row of characters: a full-body
 * avatar standing on a podium carrying the name and the figure. The information
 * is the same one for one; what changed is that a table of four Mikes can now
 * tell which is which from across the room, which is the job a name at eleven
 * pixels was doing badly. §46, §19
 *
 * It also used to carry the table's received/paid balance with a tick beside it.
 * That readout is gone, and the room went to the scores. It was proof of an
 * invariant rather than information — it read "+38 / -38 ✓" all night and "0 / 0
 * ✓" whenever a hand had just been dealt, which is to say it told a table that
 * cannot help but balance that it had balanced. The check itself has not gone
 * anywhere; it is asserted on the server, where a failure is a bug to be logged
 * rather than a glyph for a player to interpret. And drinking mode would have
 * made it actively wrong: a table that is *meant* not to balance would have
 * carried a permanent warning triangle. §14.1, §14.3, §4.6
 *
 * Every score comes from the server's totals; nothing here adds anything up.
 * §15.1, §25
 *
 * **It takes what the entry zone did not need, and never the other way round.**
 * The zone below is guaranteed at least `--control-region-share` of the screen
 * (§18.2), so this is `flex-1` against whatever is left — which is what makes a
 * table of eight fit a 320 px phone without a single key moving. The roster
 * responds by getting smaller, because it is the only thing on this screen that
 * can afford to. §18.2, §45
 */

import { useEffect, useRef, type CSSProperties } from 'react';
import {
  MIN_PLAYERS_TO_START,
  formatHandLabel,
  rosterLayout,
} from '@card-counter/shared';
import { useSession } from '@/hooks/useSession';
import {
  useActiveRound,
  useHandProgress,
  useLastRoundSummary,
  usePlayersBySeat,
  useSessionStore,
} from '@/stores/session-store';
import { useRoundExplosion } from '@/stores/ui-store';
import {
  JACKPOT_BOUNCE_EASING,
  JACKPOT_BOUNCE_STAGGER_MS,
  JACKPOT_DURATION_MS,
  JACKPOT_IMPACT_BOUNCE,
} from './jackpot';
import { PlayerPodium } from './PlayerPodium';

/**
 * How long a name may be, by how many of them share a row. §8.6, §45
 *
 * The stylesheet ellipsises anything longer anyway; this is what stops a row of
 * four from being four ellipses. Keyed on the roster's own column count rather
 * than on the head count, because that is what actually decides how wide a
 * podium is: five players and six players both lay out three across.
 */
function nameLimitFor(columns: number): number {
  if (columns >= 4) return 7;
  if (columns === 3) return 9;
  return 12;
}

/**
 * The roster's answer to a hand turning over. SPEC.md §4.4, §20.4
 *
 * A dip, a rise past where it started, and a settle — five pixels at its widest
 * and over in the same half second as the cards flying over it. It is what makes
 * the burst land *on the table* rather than float in front of it.
 *
 * Driven from here rather than by a class the explosion layer sets, because the
 * layer does not own these elements and reaching across the DOM for them would
 * make two components responsible for one. Driven by `Element.animate` rather
 * than by a CSS class for a plainer reason: toggling a class off and on inside
 * one React commit does not restart a CSS animation, so the second hand of the
 * night would be the first one that did not bounce. §4.5
 *
 * Transform-only, and staggered by a few milliseconds per unit so the roster
 * reads as one surface being struck rather than everything jumping at once. No
 * podium, name, score or key moves by a pixel — the keypad underneath is live
 * throughout, and somebody is mid-report on it. §18.4, §20.4
 */
function useImpactBounce(): (element: HTMLElement | null) => void {
  const explosion = useRoundExplosion();
  const grid = useRef<HTMLElement | null>(null);
  const bounced = useRef<string | null>(null);

  useEffect(() => {
    const element = grid.current;
    if (!element || !explosion || bounced.current === explosion.id) return;
    bounced.current = explosion.id;

    // The one preference that can switch it off. The blanket rule in index.css
    // governs CSS animations and cannot reach an `animate()` call. §19
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    element.querySelectorAll<HTMLElement>('.roster-unit').forEach((unit, index) => {
      // Decoration never throws: the roster is the only place on this screen
      // anybody's score is shown, and it may not go down with a flourish. §14.1
      if (typeof unit.animate !== 'function') return;
      unit.animate(JACKPOT_IMPACT_BOUNCE, {
        duration: JACKPOT_DURATION_MS,
        delay: index * JACKPOT_BOUNCE_STAGGER_MS,
        easing: JACKPOT_BOUNCE_EASING,
        // Not `both`: the last keyframe is where the unit already was, so there
        // is nothing to hold — and a held fill would leave a transform on every
        // player for the rest of the night.
        fill: 'none',
      });
    });
  }, [explosion]);

  return (element) => {
    grid.current = element;
  };
}

export function TableSummary() {
  const players = usePlayersBySeat();
  const round = useActiveRound();
  const lastSummary = useLastRoundSummary();
  const entries = useSessionStore((state) => state.handEntries);
  const { winnerPlayerId } = useHandProgress();
  const { startHand, interactive } = useSession();

  // The hand that just finished stays on screen until the next one starts. §7.3
  const shownRound = round ?? lastSummary?.round ?? null;
  const reportedIds = new Set(entries.map((entry) => entry.playerId));
  const canDeal = interactive && players.length >= MIN_PLAYERS_TO_START;

  /**
   * The arrangement, from the shared rule so every device lays the same table
   * out the same way. Two, three and four are one centred row, five is three
   * over two, eight is four over four — and a short row is centred rather than
   * left against the edge. §45, §60
   */
  const layout = rosterLayout(players.length);
  const nameMax = nameLimitFor(layout.columns);
  const gridRef = useImpactBounce();

  return (
    <section
      aria-label="Table"
      className="roster-panel entry-stack flex min-h-0 flex-1 flex-col rounded-card bg-panel px-2 py-1.5 text-panel-ink shadow-card"
      /* On the panel and not on the grid, because the floor under a roster has
         to be a floor the flex algorithm can see. §18.2 */
      style={{ '--roster-rows': layout.rowSizes.length } as CSSProperties}
    >
      {/* One row: which hand, and — when there is none to play — the control
          that deals the next. §4.3, §14.1 */}
      <div className="flex shrink-0 items-center justify-between gap-3">
        {/* Inset from the panel's own corner rather than sat in it. The panel's
            padding is shared with the roster below, which wants every pixel of
            it; two more here are the label's alone and cost one pixel a row. §19 */}
        <p
          data-numeric
          className="min-w-0 truncate pt-0.5 pl-1 text-[0.7rem] leading-none font-semibold tracking-[0.1em] text-panel-muted"
        >
          {shownRound ? formatHandLabel(shownRound.number) : 'NO HAND YET'}
          {shownRound && !round ? ' · ENDED' : ''}
        </p>

        {/*
          The boundary, for a table that cannot see the cards. §19, §4.4

          The burst says a hand ended and says it entirely in light and motion,
          which is the whole of what it says to a screen reader: nothing. The line
          above it is not an answer either — it is a label rather than a live
          region, so it changes silently. This is the same fact in the one form
          assistive technology is given it, and it is deliberately not a notice:
          nothing appears, nothing has to be dismissed, and no sighted player sees
          a word. §4.4, §51

          `polite`, so it waits for whatever the player was being told rather than
          cutting across a report they have just made. It is keyed on the hand, so
          it speaks once per boundary and not once per re-render.
        */}
        <p aria-live="polite" className="sr-only">
          {round
            ? `Hand ${String(round.number)} dealt`
            : shownRound
              ? `Hand ${String(shownRound.number)} ended`
              : ''}
        </p>

        {/*
          Only ever "Deal", and only when there is no hand to play. §4.3
          A hand ends itself the moment the table has told it enough and the next
          one is dealt in the same breath, so "Next hand" had exactly one correct
          moment to be pressed and every table pressed it late. What is left is
          the first hand of the night, and the hand after the host has closed one
          by hand. §5.1, §18.2
        */}
        {round ? null : (
          <button
            type="button"
            disabled={!canDeal}
            onClick={() => {
              void startHand();
            }}
            className="min-h-tap shrink-0 rounded-control border border-panel-line px-3 text-xs font-semibold text-panel-ink transition-[transform,background-color] duration-150 ease-out-soft active:scale-95 active:bg-white/10 disabled:opacity-40"
          >
            Deal
          </button>
        )}
      </div>

      {/*
        The measured box every unit is sized against. It is a size container, so
        the podiums answer to how much room the roster actually got rather than
        to how tall the phone is — a tall phone hands the roster exactly as
        little as a short one when the table is full. §18.2, §45
      */}
      <div className="roster-scope min-h-0 flex-1">
        <ul
          ref={gridRef}
          aria-label="Scores"
          /* The panel keeps its own scheme in both themes, so the dealer's glow
             takes the panel's orange here rather than the page's. §20.3 */
          className="roster-grid [--dealer-ring:var(--color-panel-dealer)]"
          /* Tracks lay the grid out; columns are what a *cell* is a share of, and
             the stylesheet needs both to size a character from the room it
             actually has rather than from the head count. §45, §18.3 */
          style={
            {
              '--roster-tracks': layout.tracks,
              '--roster-columns': layout.columns,
            } as CSSProperties
          }
        >
          {players.map((player, index) => {
            const slot = layout.slots[index];
            if (!slot) return null;
            return (
              <PlayerPodium
                key={player.id}
                playerId={player.id}
                name={player.name}
                avatarKey={player.avatarKey}
                reported={reportedIds.has(player.id)}
                isWinner={player.id === winnerPlayerId}
                slot={slot}
                nameMax={nameMax}
              />
            );
          })}
        </ul>
      </div>
    </section>
  );
}
